import { FC, ReactNode, useEffect } from "react";
import Head from "next/head";
import useSeoContent from "src/hooks/useSeoContent";
import { useRouter } from "next/router";
import { localeDomainMapV2 } from "src/utils/domains";
import {
  buildHrefLink,
  getLocalizedPath,
  parsePathInfo,
} from "src/modules/page/utils";
import { getBaseUrl } from "src/utils/getBaseUrl";
import {
  resolveCanonicalPath,
  resolvePaginationLinks,
  toPublicBrandPath,
} from "src/utils/pagination/plp-paths";
import { toPublicAsPath } from "src/utils/pagination/page-url";
import { locale as appLocale } from "src/config/app-config";
import { SeoQueryResponse } from "src/types/graphql.d";
import type { Locale } from "src/lib/Intl";

const localesUsingDomainAsHreflang = ["pl-pl", "es-es", "it-it"];

interface Props {
  title?: string;
  deeplink?: string;
  metaTags?: ReactNode;
  description?: string;
  scripts?: ReactNode;
  noindex?: boolean;
  isPDP?: boolean;
  enableIndexation?: boolean;
  skipSeoContent?: boolean;
  hreflangCategorySlugs?: Record<string, string>;
  includeCanonical?: boolean;
  seoContent?: SeoQueryResponse | null;
  totalPages?: number;
}

const PageHead: FC<Props> = ({
  title,
  deeplink,
  metaTags,
  description,
  noindex = true,
  scripts,
  isPDP,
  enableIndexation,
  skipSeoContent = false,
  hreflangCategorySlugs,
  includeCanonical = false,
  seoContent: seoContentProp,
  totalPages,
}) => {
  const metaRobotsContent = `follow${noindex ? ",noindex" : ""}`;
  const router = useRouter();
  const asPath = toPublicAsPath(router.asPath);
  const { pageUrlSlug, canonicalPath, paginatedPage } =
    resolveCanonicalPath(asPath);

  const { data } = useSeoContent({
    page_url_slug: pageUrlSlug,
    skipSeoContent: skipSeoContent || Boolean(seoContentProp),
  });
  const seoContent = seoContentProp || data?.getSeoContent;

  const locale = appLocale?.toLowerCase() as Locale;
  const toAbsoluteUrl = (path: string) =>
    `${getBaseUrl(locale)}${toPublicBrandPath(path, locale)}`;
  const localizedUrlSlug = getLocalizedPath(pageUrlSlug, locale) ?? pageUrlSlug;
  const canonicalHref =
    (!paginatedPage && seoContent?.canonical_tags) ||
    toAbsoluteUrl(
      `${localizedUrlSlug}${canonicalPath.slice(pageUrlSlug.length)}`
    );

  const { prevPath, nextPath } = resolvePaginationLinks(
    localizedUrlSlug,
    paginatedPage ?? 1,
    totalPages
  );

  const pathInfo = parsePathInfo(asPath);
  const hrefLangs = Object.entries(localeDomainMapV2).map(
    ([locale, domain]) => {
      const hrefLink = buildHrefLink(
        pathInfo,
        [locale, domain],
        hreflangCategorySlugs
      );
      if (!hrefLink) return null;
      const hreflangValue = localesUsingDomainAsHreflang.includes(locale)
        ? domain
        : locale;
      return (
        <link
          rel="alternate"
          href={hrefLink}
          hrefLang={hreflangValue}
          key={locale}
        />
      );
    }
  );

  const robotsTagValue = seoContent?.robots_tag_value
    ? seoContent.robots_tag_value
    : isPDP || enableIndexation
    ? "follow,index"
    : metaRobotsContent;

  const descriptionContent = seoContent?.meta_description || description;

  const removeMetaTags = (selector: string) => {
    const existingTags = document.querySelectorAll(selector);
    existingTags.forEach((el) => el.remove());
  };

  useEffect(() => {
    if (seoContent?.meta_description) {
      removeMetaTags('meta[name="description"]');
      removeMetaTags('meta[property="og:description"]');
    }
  }, [seoContent]);

  return (
    <Head>
      <title>{seoContent?.meta_title || title}</title>
      {descriptionContent && (
        <meta
          key="description"
          name="description"
          content={descriptionContent}
        />
      )}
      {descriptionContent && (
        <meta
          key="og:description"
          property="og:description"
          content={descriptionContent}
        />
      )}
      {includeCanonical && (
        <link rel="canonical" href={canonicalHref} key="canonical" />
      )}
      {includeCanonical && prevPath && (
        <link rel="prev" href={toAbsoluteUrl(prevPath)} key="prev" />
      )}
      {includeCanonical && nextPath && (
        <link rel="next" href={toAbsoluteUrl(nextPath)} key="next" />
      )}
      {robotsTagValue && <meta name="robots" content={robotsTagValue} />}
      <meta
        name="og:image"
        content="https://www.otrium.nl/wp-content/themes/otrium/images/otrium_nl_2.png"
      />
      {deeplink && (
        <>
          <meta property="al:android:url" content={deeplink} />
          <meta property="al:ios:url" content={deeplink} />
        </>
      )}
      {metaTags}
      {!paginatedPage && hrefLangs}
      {scripts && scripts}
    </Head>
  );
};
export default PageHead;
